import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { auth, db } from "../firebase";
import {
  collection,
  deleteField,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavbar } from "./DashboardLayout";
import AddDreamCompany from "../components/addDreamCompany";
import AddTargetRole, { TargetRole } from "../components/addTargetRole";
import AddContactPerson, { ContactPerson } from "../components/addContactPerson";
import DreamCompanyCard from "../components/DreamCompanyCard";
import DreamCompanySheet, { CONTACT_STATUS_ORDER } from "../components/DreamCompanySheet";
import { TrackerSearch } from "../components/trackerHeader";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { MELT_BUTTON_QUIET, MELT_DIALOG } from "../components/ui/melt";
import { toApplications, type ApiItem } from "../utils/applicationCards";
import { detectBoard } from "../utils/dreamApi";
import { savedRolesFor, type RoleAction } from "../utils/newRoles";
import {
  DETECT_RETRY_AFTER_MS,
  MAX_DREAM_COMPANIES,
  applicationsFor,
  detectionWait,
  compareDreamCompanies,
  openRoles,
  shortTimeAgo,
  stepsOf,
  toMillis,
  unseenNewRoles,
  type DreamBoard,
  type DreamCompany,
  type Step,
} from "../utils/dreamCompanies";
import { cn } from "../lib/utils";

const SCHOOL_STORAGE_KEY = "dreamCompanies:school";

function readSchool() {
  try {
    return localStorage.getItem(SCHOOL_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function matchesSearch(c: DreamCompany, term: string) {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return (
    c.name.toLowerCase().includes(t) ||
    Boolean(c.domain?.toLowerCase().includes(t)) ||
    stepsOf(c).some((step) => !step.done && step.text.toLowerCase().includes(t)) ||
    (c.roles || []).some((r) => r.title.toLowerCase().includes(t)) ||
    openRoles(c).some((r) => r.title.toLowerCase().includes(t))
  );
}

/** "2 of your companies posted new roles · checked 1h ago", or a plain count. */
function summary(companies: DreamCompany[], boards: Record<string, DreamBoard>) {
  const withNew = companies.filter((c) => unseenNewRoles(c).length).length;
  const lastChecked = Math.max(0, ...Object.values(boards).map((b) => toMillis(b.lastCheckedAt)));
  const parts = [
    withNew
      ? `${withNew} of your companies posted new roles`
      : `${companies.length} following`,
  ];
  if (lastChecked) parts.push(`checked ${shortTimeAgo(new Date(lastChecked).toISOString())}`);
  return parts.join(" · ");
}

function RemoveCompanyDialog({
  company,
  onCancel,
  onConfirm,
}: {
  company: DreamCompany | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!company) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 pb-6 pt-7")}>
        <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">Remove company?</DialogTitle>
        <DialogDescription className="mt-2 text-sm leading-relaxed text-melt-text2">
          <span className="font-medium text-melt-text1">{company.name}</span> and the roles and people you saved for it
          will be removed. Your tracker applications stay. This can’t be undone.
        </DialogDescription>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className={MELT_BUTTON_QUIET}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-11 items-center rounded-xl bg-[#A5492E] px-5 text-[15px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Remove
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DreamCompanies() {
  const { setNavbarActions } = useNavbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(auth.currentUser);
  const [companies, setCompanies] = useState<DreamCompany[]>([]);
  const [cards, setCards] = useState<ApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<DreamCompany | null>(null);
  const [removing, setRemoving] = useState<DreamCompany | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<TargetRole | null>(null);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState<ContactPerson | null>(null);
  const [school, setSchool] = useState(readSchool);
  // Re-render each minute so "checked 1h ago" stays current
  const [minuteTick, setMinuteTick] = useState(0);

  useEffect(() => {
    const tick = window.setInterval(() => setMinuteTick((n) => n + 1), 60000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(
    () =>
      onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        if (!currentUser) setLoading(false);
      }),
    []
  );

  // Dream companies
  useEffect(() => {
    if (!user) {
      setCompanies([]);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "users", user.uid, "dreamCompanies"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setCompanies(snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as DreamCompany)));
        setLoading(false);
      },
      (error) => {
        console.error("Could not load dream companies:", error);
        setLoading(false);
      }
    );
  }, [user]);

  // Job board docs, for "checked 2h ago". One listener per board (MAX_DREAM_COMPANIES at most)
  const [boards, setBoards] = useState<Record<string, DreamBoard>>({});
  const boardKeys = useMemo(
    () => Array.from(new Set(companies.map((c) => c.boardKey).filter((k): k is string => Boolean(k)))).sort().join("|"),
    [companies]
  );
  useEffect(() => {
    if (!boardKeys) {
      setBoards({});
      return;
    }
    const keys = boardKeys.split("|");
    const unsubscribes = keys.map((key) =>
      onSnapshot(
        doc(db, "boards", key),
        (snap) => setBoards((prev) => ({ ...prev, [key]: (snap.data() as DreamBoard) || {} })),
        (error) => console.error(`Could not load board ${key}:`, error)
      )
    );
    setBoards((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => keys.includes(key))));
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [boardKeys]);

  // Job board check: the add dialog starts it, but if that call failed (backend
  // asleep or down, offline) the company would wait forever. Retry once per
  // visit for anything waiting over a minute; the panel offers "Try again" after that.
  const [detecting, setDetecting] = useState<Set<string>>(new Set());
  const retried = useRef<Set<string>>(new Set());
  const runDetection = async (id: string) => {
    setDetecting((prev) => new Set(prev).add(id));
    await detectBoard(id);
    setDetecting((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  useEffect(() => {
    companies
      .filter((c) => detectionWait(c) > DETECT_RETRY_AFTER_MS && !retried.current.has(c.id))
      .forEach((c) => {
        retried.current.add(c.id);
        runDetection(c.id);
      });
    // runDetection only touches state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, minuteTick]);

  // Current-cycle tracker cards, for each company's Apply / Interview progress
  useEffect(() => {
    if (!user) {
      setCards([]);
      return;
    }
    const q = query(collection(db, "users", user.uid, "emails"), where("archiveId", "==", null));
    return onSnapshot(
      q,
      (snapshot) => setCards(toApplications(snapshot.docs)),
      (error) => console.error("Could not load applications:", error)
    );
  }, [user]);

  // Roles saved from New roles on Home, shown on their company's panel
  const [roleActions, setRoleActions] = useState<RoleAction[]>([]);
  useEffect(() => {
    if (!user) {
      setRoleActions([]);
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "roleActions"),
      (snapshot) => setRoleActions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as RoleAction)),
      (error) => console.error("Could not load saved roles:", error)
    );
  }, [user]);

  const atLimit = companies.length >= MAX_DREAM_COMPANIES;

  const openAdd = () => {
    if (atLimit) return;
    setEditingCompany(null);
    setShowAddModal(true);
  };

  const visible = useMemo(
    () => companies.filter((c) => matchesSearch(c, search)).sort((a, b) => compareDreamCompanies(a, b)),
    [companies, search]
  );

  const applicationsById = useMemo(() => {
    const map = new Map<string, ApiItem[]>();
    companies.forEach((c) => map.set(c.id, applicationsFor(c, cards)));
    return map;
  }, [companies, cards]);

  const openCompany = companies.find((c) => c.id === openId) || null;

  // Home's watchlist links straight to a company's panel. The state is cleared
  // once used, so going back or refreshing doesn't reopen it.
  const requestedId = (location.state as { openCompany?: string } | null)?.openCompany;
  useEffect(() => {
    if (!requestedId || loading) return;
    if (companies.some((c) => c.id === requestedId)) setOpenId(requestedId);
    navigate(location.pathname, { replace: true, state: null });
  }, [requestedId, loading, companies, navigate, location.pathname]);

  // Title and tools sit in the top bar, like the tracker
  const header = (
    <div className="flex w-full min-w-0 items-center justify-between gap-4 font-grotesk">
      <div className="flex min-w-0 items-baseline gap-3">
        <h1 className="whitespace-nowrap text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">
          Dream Companies
        </h1>
        {companies.length > 0 && (
          <div className="hidden items-center gap-2 truncate whitespace-nowrap font-plex text-xs text-melt-text3 sm:flex">
            <span className="h-[7px] w-[7px] rounded-full bg-melt-caramel" />
            {summary(companies, boards)}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2.5">
        <TrackerSearch onQueryChange={setSearch} label="Search companies" placeholder="Search companies or roles" />
      </div>
    </div>
  );

  useEffect(() => {
    setNavbarActions(header);
    // `header` is rebuilt every render; these are the values it shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNavbarActions, companies, boards, minuteTick]);

  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  // ---------------------------------------------------------------- writes

  // Field paths allowed ("tracking.seenAt"), so one nested field can change on its own
  const patch = async (id: string, update: Partial<DreamCompany> | Record<string, unknown>) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, "users", user.uid, "dreamCompanies", id), update);
    } catch (error) {
      console.error("Error updating company:", error);
      alert("Failed to update company");
    }
  };

  const removeCompany = async (company: DreamCompany) => {
    if (!user) return;
    setRemoving(null);
    setOpenId(null);
    try {
      await deleteDoc(doc(db, "users", user.uid, "dreamCompanies", company.id));
    } catch (error) {
      console.error("Error deleting company:", error);
      alert("Failed to remove company");
    }
  };

  const setRoles = (roles: TargetRole[]) => openCompany && patch(openCompany.id, { roles });
  const setPeople = (people: ContactPerson[]) => openCompany && patch(openCompany.id, { people });
  const roles = openCompany?.roles || [];
  const people = openCompany?.people || [];

  const saveRole = (role: TargetRole) =>
    setRoles(roles.some((r) => r.id === role.id) ? roles.map((r) => (r.id === role.id ? role : r)) : [...roles, role]);

  const savePerson = (person: ContactPerson) =>
    setPeople(
      people.some((p) => p.id === person.id) ? people.map((p) => (p.id === person.id ? person : p)) : [...people, person]
    );

  // The whole steps list is saved at once; the older single-step fields go
  // away the first time a company's steps change
  const saveSteps = (id: string, steps: Step[]) =>
    patch(id, { steps, nextStep: deleteField(), doneSteps: deleteField() });

  // Closing a company's panel marks its new roles as seen (on close, so the
  // "new" dots stay while the user is looking at them)
  const closeSheet = () => {
    if (openCompany && unseenNewRoles(openCompany).length) {
      patch(openCompany.id, { "tracking.seenAt": new Date().toISOString() });
    }
    setOpenId(null);
  };

  const handleSchoolChange = (value: string) => {
    setSchool(value);
    try {
      localStorage.setItem(SCHOOL_STORAGE_KEY, value);
    } catch {
      // storage unavailable; it just won't be remembered
    }
  };

  // ---------------------------------------------------------------- render

  return (
    <div className="w-full font-grotesk">
      {loading ? (
        <div className="flex items-center justify-center py-12 text-melt-text3">Loading…</div>
      ) : (
        <div className="flex flex-col gap-3.5 pb-8">
          {/* Same header as a tracker column */}
          <div className="flex items-center gap-2 pr-2">
            <span className="h-2 w-2 rounded-[3px] bg-melt-caramel" />
            <h2 className="text-[13.5px] font-semibold text-melt-text1">Following</h2>
            <span className="font-plex text-xs text-melt-text3">
              {companies.length}/{MAX_DREAM_COMPANIES}
            </span>
            <button
              type="button"
              onClick={openAdd}
              disabled={atLimit}
              aria-label="Add a dream company"
              title={atLimit ? `You’re following ${MAX_DREAM_COMPANIES}. Remove one to add another.` : "Add a dream company"}
              className="ml-auto grid h-6 w-6 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Plus size={16} strokeWidth={1.75} />
            </button>
          </div>

          {companies.length === 0 || visible.length === 0 ? (
            <div className="max-w-[283px]">
                            <p className="text-[15px] font-semibold text-melt-text1">
                {companies.length === 0 ? "No dream companies yet" : "No matches"}
              </p>
              <p className="mt-1 max-w-[26ch] text-[13px] leading-[1.45] text-melt-text2">
                {companies.length === 0
                  ? `Follow up to ${MAX_DREAM_COMPANIES} places you’d love to work. Add one with +.`
                  : "No company matches your search."}
              </p>
            </div>
          ) : (
            // 4 per row on a laptop (4 · 4 · 2 at the 10-company cap), two rows of 5 on wide screens
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1760px]:grid-cols-5">
              {visible.map((company) => (
                <DreamCompanyCard
                  key={company.id}
                  company={company}
                  applications={applicationsById.get(company.id) || []}
                  board={company.boardKey ? boards[company.boardKey] : undefined}
                  onOpen={() => setOpenId(company.id)}
                  onStepsChange={(steps) => saveSteps(company.id, steps)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <DreamCompanySheet
        company={openCompany}
        applications={openCompany ? applicationsById.get(openCompany.id) || [] : []}
        board={openCompany?.boardKey ? boards[openCompany.boardKey] : undefined}
        detecting={openCompany ? detecting.has(openCompany.id) : false}
        onRetryDetection={() => openCompany && runDetection(openCompany.id)}
        onClose={closeSheet}
        onEdit={() => {
          setEditingCompany(openCompany);
          setShowAddModal(true);
        }}
        onDelete={() => setRemoving(openCompany)}
        onStepsChange={(steps) => openCompany && saveSteps(openCompany.id, steps)}
        onAddRole={() => {
          setEditingRole(null);
          setShowRoleModal(true);
        }}
        onEditRole={(role) => {
          setEditingRole(role);
          setShowRoleModal(true);
        }}
        onDeleteRole={(role) => setRoles(roles.filter((r) => r.id !== role.id))}
        onOpenTracker={() => navigate("/dashboard/tracker")}
        savedRoles={openCompany ? savedRolesFor(openCompany.name, roleActions) : []}
        onUnsaveRole={(role) =>
          user &&
          deleteDoc(doc(db, "users", user.uid, "roleActions", role.id)).catch((e) =>
            console.error("Could not remove saved role:", e)
          )
        }
        school={school}
        onSchoolChange={handleSchoolChange}
        onAddPerson={() => {
          setEditingPerson(null);
          setShowPersonModal(true);
        }}
        onEditPerson={(person) => {
          setEditingPerson(person);
          setShowPersonModal(true);
        }}
        onDeletePerson={(person) => setPeople(people.filter((p) => p.id !== person.id))}
        onCyclePersonStatus={(person) => {
          const next = CONTACT_STATUS_ORDER[(CONTACT_STATUS_ORDER.indexOf(person.status) + 1) % CONTACT_STATUS_ORDER.length];
          setPeople(people.map((p) => (p.id === person.id ? { ...p, status: next } : p)));
        }}
      />

      <AddDreamCompany
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingCompany(null);
        }}
        editCompany={editingCompany || undefined}
      />

      <AddTargetRole
        isOpen={showRoleModal}
        companyName={openCompany?.name || ""}
        editRole={editingRole}
        onClose={() => {
          setShowRoleModal(false);
          setEditingRole(null);
        }}
        onSave={saveRole}
      />

      <AddContactPerson
        isOpen={showPersonModal}
        companyName={openCompany?.name || ""}
        editPerson={editingPerson}
        onClose={() => {
          setShowPersonModal(false);
          setEditingPerson(null);
        }}
        onSave={savePerson}
      />

      <RemoveCompanyDialog
        company={removing}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && removeCompany(removing)}
      />
    </div>
  );
}
